import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  PlusIcon,
  TrashIcon,
  RefreshCwIcon,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  XIcon,
  CheckIcon,
  SearchIcon,
  ShieldCheckIcon,
  ClockIcon,
  BriefcaseBusinessIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { CheckedState } from '@radix-ui/react-checkbox';
import { FeatureGate } from 'app/components/FeatureGate';

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableHeader,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from '@shadcn/ui';
import projectServices, {
  ProjectInfoData,
  SortField,
  SortOrder,
  ClientService,
  EnergyStarMeter,
  PropertyUse,
} from 'app/services/project-services';
import { ProjectModal } from './ProjectModal';
import syncServices from 'app/services/sync-services';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import userServices from 'app/services/user-services';
import { ClientServicesInput } from './tabs/building-details/ClientServicesInput';
import { ComplianceHistoryItem } from 'app/utils/constants/buildingInfoStructure';
import useFeatureAccess from 'app/services/feature-access';

// Define the mapping between display labels and sort fields
const SORT_FIELD_LABELS: Record<SortField, string> = {
  name: 'Project Name',
  createdAt: 'Created At',
  owner: 'Owner',
  representative: 'Owner Representative',
  ll84Required: 'LL84 Compliance',
  ll97Required: 'LL97 Compliance',
};

interface TeamMember {
  id: string;
  name: string;
  email: string;
  avatar: string;
}

// Keep BuildingRepresentation interface since it's not exported from project-services
interface BuildingRepresentation {
  owner: string;
  representative: string;
  services: ClientService[];
}

interface ChildProperty {
  name: string;
  createdAt: string;
  energyStarId: string;
  energyStarMeters: EnergyStarMeter[];
  propertyUses: PropertyUse[];
  buildingRepresentation: BuildingRepresentation;
  propertyIdentifiers: {
    BBL: string[];
    BIN: string[];
  };
  complianceRequirements: {
    buildingCount: number;
    ll84: {
      requiredToReportWater: boolean;
      required: boolean;
    };
    ll97: {
      reportType: string;
      required: boolean;
    };
  };
  complianceHistory: ComplianceHistoryItem[];
  conEdisonUsagePoints: any[];
  conEdisonSubscriptionId: string;
  squareFeet: number;
  yearBuilt: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  services: string[];
}

interface ChildPropertyData extends ChildProperty {
  _id: string;
}

const formatServiceName = (service: ClientService) => {
  const serviceMap: Record<ClientService, string> = {
    ll84: 'LL84',
    ll87: 'LL87',
    ll88: 'LL88',
    ll97: 'LL97',
    'setup-espm': 'Setup ESPM',
    'setup-bms': 'Setup BMS',
    'setup-rtem': 'Setup RTEM',
  };
  return serviceMap[service] || service;
};

export const Project = () => {
  const [projects, setProjects] = useState<ProjectInfoData[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [refreshProjects, setRefreshProjects] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState({});
  const [showDeleteFeedbackType, setShowDeleteFeedbackType] = useState<'success' | 'error' | null>(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showSyncFeedbackType, setShowSyncFeedbackType] = useState<'success' | 'error' | null>(null);
  const [syncError, setSyncError] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('owner');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [selectedComplianceType, setSelectedComplianceType] = useState<'ll84' | 'll97'>('ll84');
  const [showComplianceFeedback, setShowComplianceFeedback] = useState<'success' | 'error' | null>(null);
  const [complianceError, setComplianceError] = useState<string>('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedServices, setSelectedServices] = useState<ClientService[]>([]);
  const [showServicesFeedback, setShowServicesFeedback] = useState<'success' | 'error' | null>(null);
  const [servicesError, setServicesError] = useState<string>('');
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
  const [complianceFilter, setComplianceFilter] = useState<string | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [selectedRepresentative, setSelectedRepresentative] = useState<string>('');
  const [hasReachedLimit, setHasReachedLimit] = useState(false);
  const [organizationStats, setOrganizationStats] = useState<{ totalSquareFootage: number; buildingCount: number }>({
    totalSquareFootage: 0,
    buildingCount: 0,
  });

  const { authUser } = useJumboAuth();
  const organizationId = authUser?.relationships?.organization?.data?.id;
  const { hasReachedBuildingLimit, getBuildingLimit, currentPlan } = useFeatureAccess();

  const handleNewProject = () => {
    if (hasReachedLimit) {
      window.location.href = '/account/profile';
      return;
    }
    setShowNewProjectModal(true);
  };

  const handleModalOpenChange = (newOpen: boolean) => {
    setShowNewProjectModal(newOpen);
    setRefreshProjects(!newOpen);
  };

  const getProjectPath = (project: { id: string }) => {
    return `/dashboard/project/${project.id}/building-info`;
  };

  const handleCheckboxChange = (_: CheckedState, projectId: string) => {
    setSelectedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const toggleExpandProject = (projectId: string) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteWarning(false);
    const projectsToDelete = Object.keys(selectedProjects).filter((id) => selectedProjects[id]);
    try {
      await Promise.all(projectsToDelete.map((id) => projectServices.delete(id)));
      setRefreshProjects((prev) => !prev);
      setSelectedProjects({});
      setShowDeleteFeedbackType('success');
    } catch (error) {
      setShowDeleteFeedbackType('error');
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteWarning(true);
  };

  const handleBulkSync = async () => {
    const projectsToSync = Object.keys(selectedProjects)
      .filter((id) => selectedProjects[id])
      .map((id) => projects.find((p) => p.id === id))
      .filter(
        (project): project is ProjectInfoData =>
          project !== undefined &&
          project.attributes.energyStarId !== undefined &&
          project.attributes.energyStarId !== null
      );

    try {
      for (const project of projectsToSync) {
        if (project.attributes.energyStarId) {
          await syncServices.syncWithEnergyStar(project.id, project.attributes.energyStarId);
        }
      }
      setRefreshProjects((prev) => !prev);
      setSelectedProjects({});
      setShowSyncFeedbackType('success');
    } catch (error) {
      console.error('Failed to sync projects with Energy Star:', error);
      setSyncError(error instanceof Error ? error.message : 'Failed to sync with Energy Star');
      setShowSyncFeedbackType('error');
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const buildProjectParams = ({
    page,
    limit,
    currentSortField,
    currentSortOrder,
    currentSearchQuery,
    currentComplianceFilter,
  }: {
    page: number;
    limit: number;
    currentSortField: SortField | undefined;
    currentSortOrder: SortOrder | undefined;
    currentSearchQuery: string;
    currentComplianceFilter: string | null;
  }) => {
    const params: {
      page: number;
      limit: number;
      sortField?: SortField;
      sortOrder?: SortOrder;
      search?: string;
      ll84?: string | null;
      ll97?: string | null;
    } = {
      page,
      limit,
    };

    if (currentSortField) {
      params.sortField = currentSortField;
    }
    if (currentSortOrder) {
      params.sortOrder = currentSortOrder;
    }
    if (currentSearchQuery.trim()) {
      params.search = currentSearchQuery;
    }
    if (currentComplianceFilter) {
      const [law, status] = currentComplianceFilter.split('-');
      if (law === 'll84') {
        params.ll84 = status === 'confirmed' ? 'confirmed' : 'pending';
      } else if (law === 'll97') {
        params.ll97 = status === 'confirmed' ? 'confirmed' : 'pending';
      }
    }

    return params;
  };

  const handleSearch = async () => {
    try {
      setIsSearching(true);
      const params = buildProjectParams({
        page: 1,
        limit: pagination.limit,
        currentSortField: sortField,
        currentSortOrder: sortOrder,
        currentSearchQuery: searchQuery,
        currentComplianceFilter: complianceFilter,
      });

      const projectsResponse = await projectServices.get(params);
      setProjects(projectsResponse.data);
      setPagination((prev) => ({
        ...prev,
        page: 1,
        total: projectsResponse.pagination.total,
        pages: projectsResponse.pagination.pages,
      }));
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchProjects = async () => {
      try {
        setIsSearching(true);
        const params = buildProjectParams({
          page: pagination.page,
          limit: pagination.limit,
          currentSortField: sortField,
          currentSortOrder: sortOrder,
          currentSearchQuery: searchQuery,
          currentComplianceFilter: complianceFilter,
        });

        const projectsResponse = await projectServices.get(params);
        if (isMounted) {
          setProjects(projectsResponse.data);
          setPagination((prev) => ({
            ...prev,
            total: projectsResponse.pagination.total,
            pages: projectsResponse.pagination.pages,
          }));

          const totalProjects = projectsResponse.pagination.total;
          const reached = hasReachedBuildingLimit(totalProjects);
          setHasReachedLimit(reached);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setIsSearching(false);
      }
    };

    fetchProjects();
    return () => {
      isMounted = false;
    };
  }, [refreshProjects, pagination.page, pagination.limit, sortField, sortOrder, complianceFilter, searchQuery]);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!organizationId) return;

      try {
        const data = await userServices.getTeamMembers(organizationId);
        const formattedMembers = data.map((member) => ({
          id: member.id,
          name: member.attributes?.firstName
            ? member.attributes.firstName.charAt(0).toUpperCase() + member.attributes.firstName.slice(1)
            : 'Unknown User',
        }));
        setTeamMembers(formattedMembers);
      } catch (error) {
        console.error('Error fetching team members:', error);
      }
    };

    fetchTeamMembers();
  }, [organizationId]);

  useEffect(() => {
    const fetchOrganizationStats = async () => {
      try {
        const stats = await projectServices.getOrganizationSquareFootage();
        setOrganizationStats(stats);
      } catch (error) {
        console.error('Error fetching organization stats:', error);
      }
    };

    fetchOrganizationStats();
  }, []);

  const formatSquareFootage = (sqft: number): string => {
    if (sqft >= 1000000) {
      return `${(sqft / 1000000).toFixed(1)}M`;
    } else if (sqft >= 1000) {
      return `${(sqft / 1000).toFixed(1)}K`;
    }
    return sqft.toString();
  };

  const isAnyProjectSelected = Object.values(selectedProjects).some(Boolean);

  const SortButton = ({ field }: { field: SortField }) => (
    <Button variant="ghost" onClick={() => handleSort(field)} className="flex items-center gap-1 hover:bg-transparent">
      {SORT_FIELD_LABELS[field]}
      {sortField !== field && <ArrowUpDown size={16} className="opacity-50" />}
      {sortField === field && sortOrder === 'asc' && <ArrowUp size={16} />}
      {sortField === field && sortOrder === 'desc' && <ArrowDown size={16} />}
    </Button>
  );

  const handleConfirmCompliance = async () => {
    const projectsToUpdate = Object.keys(selectedProjects).filter((id) => selectedProjects[id]);

    try {
      await Promise.all(
        projectsToUpdate.map((id) => {
          const project = projects.find((p) => p.id === id);
          const complianceHistory = project?.attributes.complianceHistory || [];
          const newComplianceHistory: ComplianceHistoryItem = {
            complianceType: selectedComplianceType,
            complianceStatus: 'confirmed',
            confirmedAt: new Date().toISOString(),
            confirmedOn: 'espm',
            confirmedBy: authUser?.id,
          };
          return projectServices.update({
            id,
            data: { complianceHistory: [...complianceHistory, newComplianceHistory] },
          });
        })
      );
      setRefreshProjects((prev) => !prev);
      setSelectedProjects({});
      setShowComplianceModal(false);
      setShowComplianceFeedback('success');
    } catch (error) {
      console.error('Failed to update compliance history:', error);
      setComplianceError(error instanceof Error ? error.message : 'Failed to update compliance history');
      setShowComplianceFeedback('error');
    }
  };

  const getComplianceStatus = (project: ProjectInfoData, type: 'll84' | 'll97') => {
    const services = project.attributes.buildingRepresentation?.services || [];

    if (!services.includes(type)) {
      return {
        status: 'N/A',
        tooltip: <p>{`${type.toUpperCase()} service not applicable for this property`}</p>,
      };
    }

    const complianceHistory = project.attributes.complianceHistory || [];
    if (project.attributes.complianceRequirements?.[type]?.required) {
      const latestCompliance = complianceHistory
        ?.filter((history) => history.complianceType === type)
        .sort((a, b) => new Date(b.confirmedAt).getTime() - new Date(a.confirmedAt).getTime())[0];

      if (latestCompliance?.complianceStatus === 'confirmed') {
        const confirmedBy = teamMembers.find((member) => member.id === latestCompliance.confirmedBy);
        const confirmedAt = new Date(latestCompliance.confirmedAt).toLocaleDateString();

        return {
          status: 'Shared with NYC DOB',
          tooltip: (
            <div className="space-y-1">
              <p>
                Confirmed by <span className="text-blue-600">{confirmedBy?.name || 'Unknown User'}</span> on{' '}
                {confirmedAt}
              </p>
              {latestCompliance.paymentConfirmationNumber && (
                <p className="text-sm text-muted-foreground">
                  Payment Confirmation: {latestCompliance.paymentConfirmationNumber}
                </p>
              )}
            </div>
          ),
        };
      }
      return {
        status: 'Required',
        tooltip: <p>{`Compliance confirmation required for ${type.toUpperCase()}`}</p>,
      };
    }
    return {
      status: 'N/A',
      tooltip: <p>{`${type.toUpperCase()} compliance not required for this property`}</p>,
    };
  };

  const handleUpdateServices = async () => {
    const projectsToUpdate = Object.keys(selectedProjects).filter((id) => selectedProjects[id]);

    try {
      await Promise.all(
        projectsToUpdate.map((id) => {
          return projectServices.update({
            id,
            data: {
              buildingRepresentation: {
                ...projects.find((p) => p.id === id)?.attributes.buildingRepresentation,
                services: selectedServices,
                owner: selectedOwner,
                representative: selectedRepresentative,
              },
            },
          });
        })
      );
      setRefreshProjects((prev) => !prev);
      setSelectedProjects({});
      setShowServicesModal(false);
      setShowServicesFeedback('success');
    } catch (error) {
      console.error('Failed to update services:', error);
      setServicesError(error instanceof Error ? error.message : 'Failed to update services');
      setShowServicesFeedback('error');
    }
  };

  const renderProjectRow = (project: ProjectInfoData, isChild = false, parentId?: string) => {
    if (!project?.attributes) {
      console.error('Attempted to render a project row with invalid data:', project);
      return null;
    }

    const hasChildren = project.attributes.childProperties && project.attributes.childProperties.length > 0;
    const isExpanded = !!expandedProjects[project.id];
    const indentClass = isChild ? 'pl-10' : '';
    const rowBgClass = isChild ? 'bg-gray-50' : '';

    return (
      <TableRow key={project.id} className={rowBgClass}>
        <TableCell className={`py-2 ${indentClass}`}>
          {!isChild && (
            <Checkbox
              checked={!!selectedProjects[project.id]}
              onCheckedChange={(c) => handleCheckboxChange(c, project.id)}
              color="primary"
              aria-label={`Select project ${project.attributes.name}`}
            />
          )}
        </TableCell>
        <TableCell className="py-2">
          <div className="flex items-center gap-1">
            {!isChild && hasChildren && (
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => toggleExpandProject(project.id)}>
                {isExpanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
              </Button>
            )}
            {!isChild && !hasChildren && <span className="inline-block w-6"></span>}
            {isChild && <span className="inline-block w-6"></span>}

            <RouterLink
              className="text-gray-900 no-underline transition-colors hover:text-primary hover:no-underline"
              to={getProjectPath(project)}
            >
              {project.attributes.name}
            </RouterLink>
          </div>
        </TableCell>
        <TableCell className="py-2 text-gray-700">
          {project.attributes.buildingRepresentation?.owner ? (
            <RouterLink
              className="text-gray-700 no-underline transition-colors hover:text-primary hover:no-underline"
              to={`/dashboard/portfolio/${encodeURIComponent(project.attributes.buildingRepresentation.owner)}/analysis`}
            >
              {project.attributes.buildingRepresentation.owner}
            </RouterLink>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell className="py-2 text-gray-700">
          {project.attributes.buildingRepresentation?.representative ? (
            <RouterLink
              className="text-gray-700 no-underline transition-colors hover:text-primary hover:no-underline"
              to={`/dashboard/portfolio/${encodeURIComponent(project.attributes.buildingRepresentation.representative)}/analysis`}
            >
              {project.attributes.buildingRepresentation.representative}
            </RouterLink>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell className="py-2 text-gray-700">
          {project.attributes.buildingRepresentation?.services?.length > 0
            ? project.attributes.buildingRepresentation.services.map(formatServiceName).join(', ')
            : '-'}
        </TableCell>
        <TableCell className="py-2 text-gray-700">
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{getComplianceStatus(project, 'll84').status}</span>
                </TooltipTrigger>
                <TooltipContent>{getComplianceStatus(project, 'll84').tooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {getComplianceStatus(project, 'll84').status === 'Shared with NYC DOB' && (
              <CheckIcon className="h-4 w-4 text-green-600" />
            )}
            {getComplianceStatus(project, 'll84').status === 'Required' && (
              <ClockIcon className="h-4 w-4 text-orange-500" />
            )}
          </div>
        </TableCell>
        <TableCell className="py-2 text-gray-700">
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{getComplianceStatus(project, 'll97').status}</span>
                </TooltipTrigger>
                <TooltipContent>{getComplianceStatus(project, 'll97').tooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {getComplianceStatus(project, 'll97').status === 'Shared with NYC DOB' && (
              <CheckIcon className="h-4 w-4 text-green-600" />
            )}
            {getComplianceStatus(project, 'll97').status === 'Required' && (
              <ClockIcon className="h-4 w-4 text-orange-500" />
            )}
          </div>
        </TableCell>
        <TableCell className="py-2">
          <div className="flex gap-2">
            {project.attributes.energyStarId ? (
              <Button
                variant="link"
                className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                asChild
              >
                <a
                  href={`https://portfoliomanager.energystar.gov/pm/property/${project.attributes.energyStarId}#summary`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline"
                >
                  ESPM Profile
                </a>
              </Button>
            ) : (
              '-'
            )}
            {project.attributes.energyStarId && !isChild && (
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-primary"
                onClick={async () => {
                  try {
                    await syncServices.syncWithEnergyStar(project.id, project.attributes.energyStarId);
                    setRefreshProjects((prev) => !prev);
                    setShowSyncFeedbackType('success');
                  } catch (error) {
                    console.error('Failed to sync with Energy Star:', error);
                    setSyncError(error instanceof Error ? error.message : 'Failed to sync with Energy Star');
                    setShowSyncFeedbackType('error');
                  }
                }}
                aria-label="Sync with Energy Star"
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <RefreshCwIcon size={16} />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Sync with Energy Star</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const renderTableRows = () => {
    // First collect all child project IDs
    const childProjectIds = new Set<string>();
    projects.forEach((project) => {
      if (project?.attributes?.childProperties) {
        (project.attributes.childProperties as unknown as ChildPropertyData[]).forEach((child) => {
          childProjectIds.add(child._id);
        });
      }
    });

    // Filter out projects that are child properties from the top level project rows
    const topLevelProjects = projects.filter((project) => !childProjectIds.has(project.id));

    return topLevelProjects.flatMap((project) => {
      if (!project?.attributes) {
        console.error('Skipping rendering for project with missing attributes:', project);
        return [];
      }

      const parentRow = renderProjectRow(project);
      const childRows =
        expandedProjects[project.id] && project.attributes.childProperties
          ? (project.attributes.childProperties as unknown as ChildPropertyData[]).map((childProject) => {
              const childProjectData: ProjectInfoData = {
                id: childProject._id,
                type: 'project',
                attributes: {
                  name: childProject.name,
                  createdAt: childProject.createdAt,
                  energyStarId: childProject.energyStarId || '',
                  energyStarMeters: childProject.energyStarMeters || [],
                  parentProperty: project.id,
                  childProperties: [],
                  propertyUses: childProject.propertyUses || [],
                  buildingRepresentation: childProject.buildingRepresentation || {
                    owner: '',
                    representative: '',
                    services: [],
                  },
                  propertyIdentifiers: childProject.propertyIdentifiers || { BBL: [], BIN: [] },
                  complianceRequirements: childProject.complianceRequirements || {
                    buildingCount: 0,
                    ll84: { requiredToReportWater: false, required: false },
                    ll97: { reportType: 'simple', required: false },
                  },
                  complianceHistory: childProject.complianceHistory || [],
                  conEdisonUsagePoints: [],
                  conEdisonSubscriptionId: childProject.conEdisonSubscriptionId || '',
                  squareFeet: childProject.squareFeet || 0,
                  yearBuilt: childProject.yearBuilt || '',
                  address: childProject.address || '',
                  city: childProject.city || '',
                  state: childProject.state || '',
                  zipCode: childProject.zipCode || '',
                  services: (childProject.services || []).map(String),
                },
              };
              return renderProjectRow(childProjectData, true, project.id);
            })
          : [];
      return [parentRow, ...childRows].filter((row) => row !== null);
    });
  };

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Header Section */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-baseline gap-3">
              <h1 className="text-2xl font-semibold text-gray-900">Projects</h1>
              <div className="flex items-center rounded-full bg-blue-50 px-3 py-1">
                <span className="text-sm font-medium text-blue-700">
                  {formatSquareFootage(organizationStats.totalSquareFootage)} sq ft
                </span>
                <span className="ml-1 text-xs text-blue-500">under management</span>
              </div>
              {organizationStats.buildingCount > 0 && (
                <div className="flex items-center rounded-full bg-green-50 px-3 py-1">
                  <span className="text-sm font-medium text-green-700">{organizationStats.buildingCount}</span>
                  <span className="ml-1 text-xs text-green-500">buildings</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 border-dashed ${hasReachedLimit ? 'cursor-not-allowed opacity-70' : ''}`}
                    onClick={handleNewProject}
                  >
                    <PlusIcon className="mr-1 h-4 w-4" />
                    New Project
                  </Button>
                </div>
              </TooltipTrigger>
              {hasReachedLimit && (
                <TooltipContent>
                  <p>
                    You've reached the limit of {getBuildingLimit()} projects for your {currentPlan} plan.
                  </p>
                  <p>Please upgrade to add more projects.</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-1 items-center gap-3">
            <div className="relative max-w-md flex-1">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <Input
                type="text"
                placeholder="Search by project name, owner, representative, BBL, BIN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="h-9 w-full pl-9 pr-4 text-sm"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transform">
                  <RefreshCwIcon className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>
            <Button variant="secondary" size="sm" onClick={handleSearch} disabled={isSearching} className="h-9">
              Search
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-gray-600 hover:text-gray-900 disabled:opacity-40"
              disabled={!isAnyProjectSelected}
              onClick={() => setShowComplianceModal(true)}
            >
              <ShieldCheckIcon className="mr-1.5 h-4 w-4" />
              <span className="text-sm">Confirm Compliance</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-gray-600 hover:text-gray-900 disabled:opacity-40"
              disabled={!isAnyProjectSelected}
              onClick={() => {
                const firstSelectedProject = projects.find((p) => selectedProjects[p.id]);
                setSelectedServices(firstSelectedProject?.attributes.buildingRepresentation?.services || []);
                setSelectedOwner(firstSelectedProject?.attributes.buildingRepresentation?.owner || '');
                setSelectedRepresentative(
                  firstSelectedProject?.attributes.buildingRepresentation?.representative || ''
                );
                setShowServicesModal(true);
              }}
            >
              <BriefcaseBusinessIcon className="mr-1.5 h-4 w-4" />
              <span className="text-sm">Update Services</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-gray-600 hover:text-gray-900 disabled:opacity-40"
              disabled={!isAnyProjectSelected}
              onClick={handleBulkSync}
            >
              <RefreshCwIcon className="mr-1.5 h-4 w-4" />
              <span className="text-sm">Sync</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-red-600 hover:text-red-700 disabled:opacity-40"
              disabled={!isAnyProjectSelected}
              onClick={handleDeleteClick}
            >
              <TrashIcon className="mr-1.5 h-4 w-4" />
              <span className="text-sm">Delete</span>
            </Button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="mt-3 flex items-center gap-3 border-t pt-3">
          <span className="text-sm font-medium text-gray-700">Filters:</span>
          <Select
            value={complianceFilter || 'all'}
            onValueChange={(value) => {
              setComplianceFilter(value === 'all' ? null : value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            <SelectTrigger className="h-8 w-[200px] text-sm">
              <SelectValue placeholder="Compliance Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Compliance Status</SelectItem>
              <SelectItem value="ll97-pending">LL97 Pending</SelectItem>
              <SelectItem value="ll97-confirmed">LL97 Confirmed</SelectItem>
              <SelectItem value="ll84-pending">LL84 Pending</SelectItem>
              <SelectItem value="ll84-confirmed">LL84 Confirmed</SelectItem>
            </SelectContent>
          </Select>
          {complianceFilter && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-gray-600"
              onClick={() => {
                setComplianceFilter(null);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            >
              <XIcon className="mr-1 h-3 w-3" />
              Clear filter
            </Button>
          )}
        </div>
      </div>
      <FeatureGate feature="portfolioAnalysis" showUpgradePrompt={true}>
        {/* Table Section */}
        <div className="flex flex-1 flex-col overflow-hidden px-6 pt-4">
          <ScrollArea orientation="vertical" className="flex-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    {(() => {
                      const topLevelProjects = projects;
                      const numSelected = topLevelProjects.filter((p) => !!selectedProjects[p.id]).length;
                      const isIndeterminate = numSelected > 0 && numSelected < topLevelProjects.length;
                      const isAllSelected = topLevelProjects.length > 0 && numSelected === topLevelProjects.length;
                      return (
                        <Checkbox
                          color="primary"
                          checked={isIndeterminate ? 'indeterminate' : isAllSelected}
                          onClick={() => {
                            const newSelectedProjects = { ...selectedProjects };
                            topLevelProjects.forEach((project) => {
                              newSelectedProjects[project.id] = !isAllSelected;
                            });
                            setSelectedProjects(newSelectedProjects);
                          }}
                          aria-label="Select all projects on this page"
                        />
                      );
                    })()}
                  </TableHead>
                  <TableHead>
                    <SortButton field="name" />
                  </TableHead>
                  <TableHead>
                    <SortButton field="owner" />
                  </TableHead>
                  <TableHead>
                    <SortButton field="representative" />
                  </TableHead>
                  <TableHead>
                    <p className="bold text-gray-700">Client Services</p>
                  </TableHead>
                  <TableHead>
                    <SortButton field="ll84Required" />
                  </TableHead>
                  <TableHead>
                    <SortButton field="ll97Required" />
                  </TableHead>
                  <TableHead>
                    <p className="bold text-gray-700"></p>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderTableRows()}</TableBody>
            </Table>
          </ScrollArea>

          <div className="flex items-center justify-between px-2 py-4">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <span>Rows per page:</span>
              <Select
                value={pagination.limit.toString()}
                onValueChange={(value) => {
                  setPagination((prev) => ({ ...prev, limit: parseInt(value), page: 1 }));
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={pagination.limit.toString()} />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50].map((pageSize) => (
                    <SelectItem key={pageSize} value={pageSize.toString()}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-700">
                Page {pagination.page} of {pagination.pages}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      </FeatureGate>

      <ProjectModal open={showNewProjectModal} setOpen={handleModalOpenChange} />

      <Dialog open={showDeleteWarning} onOpenChange={setShowDeleteWarning}>
        <DialogContent className="bg-white p-0 sm:max-w-md">
          <DialogHeader className="bg-white px-6 pt-6">
            <DialogTitle className="text-xl font-medium text-gray-800">Delete Projects</DialogTitle>
            <DialogDescription className="mt-2 text-sm text-gray-600">
              The following projects will be permanently deleted. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white px-6 py-4">
            <div className="max-h-[200px] space-y-2 overflow-y-auto rounded-md border border-red-100 bg-red-50 p-4">
              {projects
                .filter((project) => selectedProjects[project.id])
                .map((project) => (
                  <div key={project.id} className="flex items-center gap-2 text-sm text-red-900">
                    <TrashIcon className="h-4 w-4" />
                    <span>{project.attributes.name}</span>
                  </div>
                ))}
            </div>
          </div>
          <DialogFooter className="border-t bg-white px-6 py-4">
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteWarning(false)}
                className="h-9 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleDeleteConfirm} className="h-9 bg-red-600 text-white hover:bg-red-700">
                Delete Projects
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteFeedbackType !== null}>
        <DialogContent className="bg-white p-0 sm:max-w-md">
          <div className="bg-white px-6 py-6">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              {showDeleteFeedbackType === 'success' ? (
                <>
                  <div className="rounded-full bg-emerald-50 p-3">
                    <CheckIcon className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-medium text-gray-800">Success</h3>
                    <p className="text-sm text-gray-600">Project(s) deleted successfully.</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-full bg-rose-50 p-3">
                    <XIcon className="h-6 w-6 text-rose-600" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-medium text-gray-800">Error</h3>
                    <p className="text-sm text-rose-600">Failed to delete projects. Please try again.</p>
                  </div>
                </>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDeleteFeedbackType(null)}
                className="mt-2 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSyncFeedbackType !== null} onOpenChange={() => setShowSyncFeedbackType(null)}>
        <DialogContent className="bg-white p-0 sm:max-w-md">
          <div className="bg-white px-6 py-6">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              {showSyncFeedbackType === 'success' ? (
                <>
                  <div className="rounded-full bg-emerald-50 p-3">
                    <RefreshCwIcon className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-medium text-gray-800">Sync Successful</h3>
                    <p className="text-sm text-gray-600">
                      Selected projects have been successfully synchronized with Energy Star.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-full bg-rose-50 p-3">
                    <XIcon className="h-6 w-6 text-rose-600" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-medium text-gray-800">Sync Failed</h3>
                    <p className="text-sm text-rose-600">
                      {syncError || 'Failed to sync with Energy Star. Please try again.'}
                    </p>
                  </div>
                </>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowSyncFeedbackType(null)}
                className="mt-2 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showComplianceModal} onOpenChange={setShowComplianceModal}>
        <DialogContent className="bg-white p-0 sm:max-w-md">
          <DialogHeader className="bg-white px-6 pt-6">
            <DialogTitle className="text-xl font-medium text-gray-800">Confirm Compliance</DialogTitle>
            <DialogDescription className="mt-2 text-sm text-gray-600">
              Update the compliance status for the selected project(s). This action will be recorded in the compliance
              history.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white px-6 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Compliance Type</label>
              <Select
                value={selectedComplianceType}
                onValueChange={(value: 'll84' | 'll97') => setSelectedComplianceType(value)}
              >
                <SelectTrigger className="h-9 w-full border-gray-200 bg-white text-sm text-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="ll84" className="text-sm">
                    <div className="flex items-center gap-2">
                      <ShieldCheckIcon className="h-4 w-4 text-blue-600" />
                      <span className="text-gray-700">LL84 - Energy Benchmarking</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="ll97" className="text-sm">
                    <div className="flex items-center gap-2">
                      <ShieldCheckIcon className="h-4 w-4 text-blue-600" />
                      <span className="text-gray-700">LL97 - Carbon Emissions</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="border-t bg-white px-6 py-4">
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowComplianceModal(false)}
                className="h-9 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmCompliance}
                className="h-9 bg-blue-600 text-white hover:bg-blue-700"
              >
                Confirm Compliance
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showComplianceFeedback !== null} onOpenChange={() => setShowComplianceFeedback(null)}>
        <DialogContent className="bg-white p-0 sm:max-w-md">
          <div className="bg-white px-6 py-6">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              {showComplianceFeedback === 'success' ? (
                <>
                  <div className="rounded-full bg-emerald-50 p-3">
                    <ShieldCheckIcon className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-medium text-gray-800">Compliance Status Updated</h3>
                    <p className="text-sm text-gray-600">
                      The compliance status has been successfully updated for the selected project(s).
                    </p>
                  </div>
                </>
              ) : showComplianceFeedback === 'error' ? (
                <>
                  <div className="rounded-full bg-rose-50 p-3">
                    <XIcon className="h-6 w-6 text-rose-600" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-medium text-gray-800">Update Failed</h3>
                    <p className="text-sm text-rose-600">
                      {complianceError || 'Failed to update compliance status. Please try again.'}
                    </p>
                  </div>
                </>
              ) : null}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowComplianceFeedback(null)}
                className="mt-2 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showServicesModal} onOpenChange={setShowServicesModal}>
        <DialogContent className="bg-white p-0 sm:max-w-2xl">
          <DialogHeader className="bg-white px-6 pt-6">
            <DialogTitle className="text-xl font-medium text-gray-800">Update Client Services</DialogTitle>
            <DialogDescription className="mt-2 text-sm text-gray-600">
              Update the client services, owner, and representative for the selected project(s). This will override any
              existing values.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 bg-white px-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600">Owner</label>
                <Input
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                  placeholder="Enter owner name"
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600">Representative</label>
                <Input
                  value={selectedRepresentative}
                  onChange={(e) => setSelectedRepresentative(e.target.value)}
                  placeholder="Enter representative name"
                  className="h-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Client Services</label>
              <ClientServicesInput
                selectedServices={selectedServices}
                onChange={setSelectedServices}
                className="!grid-cols-3"
              />
            </div>
          </div>
          <DialogFooter className="border-t bg-white px-6 py-4">
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowServicesModal(false)}
                className="h-9 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleUpdateServices} className="h-9 bg-blue-600 text-white hover:bg-blue-700">
                Update Services
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showServicesFeedback !== null} onOpenChange={() => setShowServicesFeedback(null)}>
        <DialogContent className="bg-white p-0 sm:max-w-md">
          <div className="bg-white px-6 py-6">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              {showServicesFeedback === 'success' ? (
                <>
                  <div className="rounded-full bg-emerald-50 p-3">
                    <BriefcaseBusinessIcon className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-medium text-gray-800">Services Updated</h3>
                    <p className="text-sm text-gray-600">
                      Client services have been successfully updated for the selected project(s).
                    </p>
                  </div>
                </>
              ) : showServicesFeedback === 'error' ? (
                <>
                  <div className="rounded-full bg-rose-50 p-3">
                    <XIcon className="h-6 w-6 text-rose-600" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-medium text-gray-800">Update Failed</h3>
                    <p className="text-sm text-rose-600">
                      {servicesError || 'Failed to update services. Please try again.'}
                    </p>
                  </div>
                </>
              ) : null}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowServicesFeedback(null)}
                className="mt-2 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
